/**
 * 模板模块 **URL 路由状态**（主 tab / subtab / 大类 query.category）。
 *
 * @remarks `ViralTemplateCategory` 此处仅表示 **URL query**（`ugc` / `metaTopAds`）。
 * 列表接口的 **业务分类** 使用打平的字符串路径，见 {@link VIRAL_LIST_CATEGORY_BY_TYPE_TAB}、{@link VIRAL_LIST_CATEGORY_ADS}。
 */
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

import { ViralTemplateEntryFromEnum } from '@/utils/eventlog/eventNewConfig';
import { viralTemplateWorkbenchURL } from '@/utils/routeConstants';

export enum ViralTemplateTab {
  LIST = 'list',
  MY_CREATIONS = 'myCreations',
}

export enum ViralTemplateTypeTab {
  TRENDING = 'trending',
  HOOK = 'hook',
  HEALTH = 'health',
  PERSONAL_CARE = 'personal_care',
}

/** 列表分页接口 `category`：UGC 下四类与 URL `subtab` 一一对应；TRENDING 不传 category */
export const VIRAL_LIST_CATEGORY_BY_TYPE_TAB: Record<
  ViralTemplateTypeTab,
  string | undefined
> = {
  [ViralTemplateTypeTab.HOOK]: '/ugc/1',
  [ViralTemplateTypeTab.PERSONAL_CARE]: '/ugc/2',
  [ViralTemplateTypeTab.HEALTH]: '/ugc/3',
  [ViralTemplateTypeTab.TRENDING]: undefined,
};

/** 列表分页接口 `category`：Meta Top Ads 等 */
export const VIRAL_LIST_CATEGORY_ADS = '/ads';

/**
 * URL query `category` 大类 slug，新增大类只改这一处 + {@link VIRAL_LIST_CATEGORY_ROUTE}
 */
export const ViralTemplateCategory = {
  UGC: 'ugc',
  META_TOP_ADS: 'metaTopAds',
} as const;

export type ViralTemplateCategory =
  (typeof ViralTemplateCategory)[keyof typeof ViralTemplateCategory];

const ROUTE_CATEGORY_VALUES = Object.values(
  ViralTemplateCategory,
) as ViralTemplateCategory[];

export function parseViralTemplateRouteCategory(
  raw: string | undefined,
): ViralTemplateCategory | undefined {
  if (!raw) return undefined;
  return ROUTE_CATEGORY_VALUES.includes(raw as ViralTemplateCategory)
    ? (raw as ViralTemplateCategory)
    : undefined;
}

/** list 路由：是否在 URL 上保留 `subtab`（多小类大类需要；固定小类的大类不需要） */
const VIRAL_LIST_CATEGORY_ROUTE: Record<
  ViralTemplateCategory,
  { persistSubtabInQuery: boolean }
> = {
  [ViralTemplateCategory.UGC]: { persistSubtabInQuery: true },
  [ViralTemplateCategory.META_TOP_ADS]: { persistSubtabInQuery: false },
};

export function listRouteCategoryUsesSubtabQuery(cat: ViralTemplateCategory): boolean {
  return VIRAL_LIST_CATEGORY_ROUTE[cat].persistSubtabInQuery;
}

export const ViralTemplateTabs = [
  { key: ViralTemplateTypeTab.TRENDING, labelKey: 'home.tpl.trending' },
  { key: ViralTemplateTypeTab.HOOK, labelKey: 'home.tpl.hook' },
  { key: ViralTemplateTypeTab.HEALTH, labelKey: 'home.tpl.health' },
  { key: ViralTemplateTypeTab.PERSONAL_CARE, labelKey: 'home.tpl.personal.care' },
];

export function resolveViralTemplateTypeTabByListCategory(
  listCategory?: string,
): ViralTemplateTypeTab {
  const matchedEntry = Object.entries(VIRAL_LIST_CATEGORY_BY_TYPE_TAB).find(
    ([, value]) => value === listCategory,
  );

  if (!matchedEntry) {
    return ViralTemplateTypeTab.TRENDING;
  }

  return matchedEntry[0] as ViralTemplateTypeTab;
}

const VALID_SUBTABS = ViralTemplateTabs.map((item) => item.key);
const isValidSubtab = (value: string | undefined): value is ViralTemplateTypeTab =>
  Boolean(value) && VALID_SUBTABS.includes(value as ViralTemplateTypeTab);
const VALID_WORKBENCH_FROM_VALUES = Object.values(ViralTemplateEntryFromEnum);
export const isValidViralTemplateEntryFrom = (
  value: string | undefined,
): value is ViralTemplateEntryFromEnum =>
  Boolean(value) &&
  VALID_WORKBENCH_FROM_VALUES.includes(value as ViralTemplateEntryFromEnum);

const VALID_MAIN_TABS = [ViralTemplateTab.LIST, ViralTemplateTab.MY_CREATIONS] as const;

const isValidMainTab = (tab: string | undefined): tab is ViralTemplateTab => {
  return !!tab && VALID_MAIN_TABS.includes(tab as ViralTemplateTab);
};

/**
 * 模板「一级」UGC 概览：`tab=list`、无 `category`；可选 `subtab`（UGC 二级返回一级时与列表小类对齐）。
 */
export function buildViralTemplateUgcOverviewQuery(
  subtab: ViralTemplateTypeTab = ViralTemplateTypeTab.TRENDING,
): Record<string, string> {
  return {
    tab: ViralTemplateTab.LIST,
    subtab,
  };
}

/** 模板「二级」UGC 全量列表：`category=ugc` + `subtab`（与路由 `currentSubtab`、列表请求一致） */
export function buildViralTemplateUgcFullListQuery(
  subtab: ViralTemplateTypeTab = ViralTemplateTypeTab.TRENDING,
): Record<string, string> {
  return {
    tab: ViralTemplateTab.LIST,
    category: ViralTemplateCategory.UGC,
    subtab,
  };
}

export function buildViralTemplateWorkbenchQuery(
  templateId?: string | number,
  subtab: ViralTemplateTypeTab = ViralTemplateTypeTab.TRENDING,
  from?: ViralTemplateEntryFromEnum,
): Record<string, string> {
  const query: Record<string, string> = {
    subtab,
  };

  if (templateId != null && String(templateId).trim()) {
    query.templateId = String(templateId).trim();
  }

  if (from) {
    query.from = from;
  }

  return query;
}

export function buildViralTemplateWorkbenchRoute(
  templateId: string | number,
  subtab: ViralTemplateTypeTab = ViralTemplateTypeTab.TRENDING,
  from?: ViralTemplateEntryFromEnum,
) {
  return {
    pathname: viralTemplateWorkbenchURL,
    query: buildViralTemplateWorkbenchQuery(templateId, subtab, from),
  };
}

function normalizeWorkbenchQuery(query: Record<string, unknown>): Record<string, string> {
  const subtab = isValidSubtab(query.subtab as string | undefined)
    ? (query.subtab as ViralTemplateTypeTab)
    : ViralTemplateTypeTab.TRENDING;
  const templateId = String(query.templateId ?? '').trim();
  const from = isValidViralTemplateEntryFrom(query.from as string | undefined)
    ? (query.from as ViralTemplateEntryFromEnum)
    : undefined;

  return buildViralTemplateWorkbenchQuery(templateId || undefined, subtab, from);
}

const buildQueryByTab = (
  tab: ViralTemplateTab,
  subtab?: ViralTemplateTypeTab,
  category?: ViralTemplateCategory,
): Record<string, string> => {
  if (tab === ViralTemplateTab.LIST) {
    const query: Record<string, string> = buildViralTemplateUgcOverviewQuery(subtab);

    if (category) query.category = category;

    if (category && listRouteCategoryUsesSubtabQuery(category)) {
      query.subtab = subtab || ViralTemplateTypeTab.TRENDING;
    }

    return query;
  }

  return { tab: ViralTemplateTab.MY_CREATIONS };
};

interface UseViralTemplateRouteStateOptions {
  onBeforeTabChange?: () => void;
}

export const useViralTemplateRouteState = (
  options: UseViralTemplateRouteStateOptions = {},
) => {
  const { onBeforeTabChange } = options;
  const router = useRouter();

  const activeMainTab = useMemo(() => {
    const tabParam = router.query.tab as string | undefined;
    return isValidMainTab(tabParam) ? tabParam : ViralTemplateTab.LIST;
  }, [router.query.tab]);

  const currentSubtab = useMemo(() => {
    return isValidSubtab(router.query.subtab as string | undefined)
      ? (router.query.subtab as ViralTemplateTypeTab)
      : ViralTemplateTypeTab.TRENDING;
  }, [router.query.subtab]);

  const currentTemplateId = useMemo(() => {
    const raw = router.query.templateId;
    return raw == null ? undefined : String(raw).trim() || undefined;
  }, [router.query.templateId]);

  const currentCategory = useMemo(() => {
    return parseViralTemplateRouteCategory(router.query.category as string | undefined);
  }, [router.query.category]);

  useEffect(() => {
    if (!router.isReady) return;
    const currentQuery = router.query as Record<string, unknown>;
    const normalizedQuery = normalizeWorkbenchQuery(currentQuery);

    const hasLegacyKeys =
      typeof currentQuery.tab === 'string' || typeof currentQuery.category === 'string';
    const subtabChanged =
      String(currentQuery.subtab ?? '') !== String(normalizedQuery.subtab ?? '');
    const templateIdChanged =
      String(currentQuery.templateId ?? '') !== String(normalizedQuery.templateId ?? '');
    const shouldNormalize =
      hasLegacyKeys || subtabChanged || templateIdChanged || !currentQuery.subtab;

    if (!shouldNormalize) return;

    router.replace(
      {
        pathname: router.pathname,
        query: normalizedQuery,
      },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const handleMainTabChange = useCallback(
    (key: string) => {
      onBeforeTabChange?.();

      router.push(
        {
          pathname: router.pathname,
          query:
            key === ViralTemplateTab.MY_CREATIONS
              ? { tab: ViralTemplateTab.MY_CREATIONS }
              : buildViralTemplateWorkbenchQuery(currentTemplateId, currentSubtab),
        },
        undefined,
        { shallow: true },
      );
    },
    [currentSubtab, currentTemplateId, onBeforeTabChange, router],
  );

  return {
    activeMainTab,
    currentSubtab,
    currentTemplateId,
    currentCategory,
    handleMainTabChange,
    isRouterReady: router.isReady,
  };
};
